import { clampTargetPolycountPatch } from 'Pods/ThreeDStudio/Services/clampTargetPolycountPatch'
import { hasPolycountField } from 'Pods/ThreeDStudio/Services/hasPolycountField'
import { polyCountForValue } from 'Pods/ThreeDStudio/Services/polyCountForValue'
import { resolveActivePolycountRange } from 'Pods/ThreeDStudio/Services/resolveActivePolycountRange'
import { getNextGenerationSchema } from 'Pods/ThreeDStudio/Shared/Utils/getNextGenerationSchema'

import {
  DEFAULT_MODEL_SETTINGS,
  POLY_COUNT,
  DEFAULT_VIEWER,
  DEFAULT_REMESH_SETTINGS,
  DEFAULT_SCHEMA,
  DEFAULT_GENERATION,
  DEFAULT_UI,
  ACTION,
  IMAGE_ASSISTANT_TAB,
} from '../constants'
import { threeDStudioStore } from '../index'
import { activeRunStateOf } from './generation'
import { setGenerationProvider } from './schema'

const setModelSettingsState = (patch) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...state.modelSettings, ...patch },
  }))
}

/**
 * Resets all model settings to their default values.
 */
export const clearModelSettings = () => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...DEFAULT_MODEL_SETTINGS },
    viewer: { ...DEFAULT_VIEWER },
    remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
    schema: { ...DEFAULT_SCHEMA },
    ui: {
      ...state.ui,
      openedModel: {},
      isModelSettingsOpen: true,
      isViewerSidebarOpen: false,
      activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
      // The assistant belonged to the composition being discarded, so it closes with it.
      // This action empties openedModel itself, so the URL-sync clear that follows the
      // navigation to /ai is skipped by its own guard - the reset has to happen here.
      isImageAssistantOpen: false,
      imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
      editImage: null,
      // The new composition brings its own session; restoring the previous conversation
      // over it would carry the discarded model's images into the new panel.
      imageAssistantSessionId: undefined,
    },
  }))
}

/**
 * Sets the prompt
 * @param {string} prompt - User prompt for generation
 */
export const setPrompt = (prompt) => {
  setModelSettingsState({ prompt })
}

/**
 * Sets the polygon count
 * @param {string} polyCount - One of: "low", "medium", "high"
 * @param {boolean} skipSelectedUseCaseUpdate - Whether to skip updating the selected use case
 */
export const setPolyCount = (polyCount, skipSelectedUseCaseUpdate = false) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, isSmartLowPoly } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  if (range) {
    const { minimum, maximum, default: defaultValue } = range
    const targetPolyCount = {
      [POLY_COUNT.LOW]: minimum,
      [POLY_COUNT.MEDIUM]: defaultValue,
      [POLY_COUNT.HIGH]: maximum,
    }[polyCount]

    if (skipSelectedUseCaseUpdate === true) {
      setModelSettingsState({ polyCount, targetPolycount: targetPolyCount })

      return
    }

    setModelSettingsState({
      polyCount,
      targetPolycount: targetPolyCount,
      selectedUseCase: undefined,
    })

    return
  }

  setModelSettingsState({ polyCount, selectedUseCase: undefined })
}

/**
 * Sets the generate type
 * @param {string} generateType - One of: "normal", "lowPoly" or undefined
 */
export const setGenerateType = (generateType) => {
  setModelSettingsState({ generateType })
}

/**
 * Sets the material type
 * @param {string} material - One of: "pbr", "basic", "geometry_only"
 */
export const setMaterial = (material) => {
  setModelSettingsState({ material, selectedUseCase: undefined })
}

/**
 * Sets the polygon type
 * @param {string} polygonType - One of: "quadrilateral", "triangle" or undefined
 */
export const setPolygonType = (polygonType) => {
  setModelSettingsState({ polygonType, selectedUseCase: undefined })
}

/**
 * Sets the target polygon count
 * @param {number|undefined} targetPolycount - Number or empty string
 */
export const setTargetPolycount = (targetPolycount) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, isSmartLowPoly } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  // Gated on the field, not the range: a schema that declares a polycount but
  // resolves no bounds can match no button, so the label has to clear.
  let polyCount = modelSettings.polyCount
  if (hasPolycountField(schema?.generationModelSchema) && targetPolycount !== '' && targetPolycount != null) {
    polyCount = polyCountForValue(Number(targetPolycount), range)
  }

  setModelSettingsState({ targetPolycount, polyCount, selectedUseCase: undefined })
}

/**
 * Sets the input image (asset ID)
 * @param {number|undefined} id - Input image asset id
 * @param {string|undefined} url - Input image URL (e.g. blob URL or file URL)
 */
export const setInputImage = (id, url) => {
  if (id === undefined) {
    threeDStudioStore.setState((state) => ({
      modelSettings: {
        ...state.modelSettings,
        inputImage: undefined,
        inputImageUrl: undefined,
      },
    }))

    return
  }

  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputImage: id,
      inputImageUrl: url,
    },
    schema: {
      ...state.schema,
      generationAction: ACTION.IMAGE_TO_3D,
    },
    generation: {
      ...state.generation,
      sessionId: undefined,
      runId: undefined,
    },
  }))
}

/**
 * Sets the input threeD (asset ID)
 * @param {number|undefined} id - Input threeD asset id
 * @param {string|undefined} url - Input threeD URL (e.g. blob URL or file URL)
 * @param {number|undefined} size - Input threeD size in bytes
 * @param {string|undefined} format - Input threeD format (e.g. "glb", "fbx", "obj", "stl")
 */
export const setInputThreeD = (id, url, size, format) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputThreeDUrl: url,
    },
    // Reset generation outcome state, but keep in-flight runs (and the scalar
    // mirror of the latest one) alive - concurrent generations must survive an
    // unrelated model upload.
    generation: { ...DEFAULT_GENERATION, ...activeRunStateOf(state.generation) },
    viewer: {
      ...state.viewer,
      inputThreeD: id,
      fileFormat: format,
      fileSize: size,
    },
  }))
}

/**
 * Sets the input threeD URL
 * @param {string|undefined} inputThreeDUrl - Input threeD URL (e.g. blob URL or file URL)
 */
export const setInputThreeDUrl = (inputThreeDUrl) => {
  setModelSettingsState({ inputThreeDUrl })
}

/**
 * Sets the input threeD format (file extension) for blob URLs
 * @param {string|undefined} inputThreeDFormat - One of: "glb", "fbx", "obj", "stl"
 */
export const setInputThreeDFormat = (inputThreeDFormat) => {
  setModelSettingsState({ inputThreeDFormat })
}

/**
 * Sets the private flag
 * @param {boolean} isPrivate - Whether the content should be private
 */
export const setIsPrivate = (isPrivate) => {
  setModelSettingsState({ isPrivate })
}

/**
 * Sets the smart low poly flag
 * @param {boolean} isSmartLowPoly - Whether smart low poly is enabled
 */
export const setIsSmartLowPoly = (isSmartLowPoly) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, targetPolycount } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  // The Polycount control clamps too, but it is unmounted while the settings
  // panel is collapsed - and that is where this toggle also lives.
  setModelSettingsState({
    isSmartLowPoly,
    selectedUseCase: undefined,
    ...(isSmartLowPoly && { extraQuality: false }),
    ...clampTargetPolycountPatch(targetPolycount, range),
  })
}

/**
 * Sets the extra quality flag. No use case preset covers extra quality, so enabling it
 * takes the settings outside the selected preset and deselects it. It also turns smart
 * low poly off: the two are mutually exclusive, since the backend forces ultra_mode off
 * on LowPoly runs.
 * @param {boolean} extraQuality - Whether extra quality is enabled
 */
export const setExtraQuality = (extraQuality) => {
  setModelSettingsState({
    extraQuality,
    ...(extraQuality && { isSmartLowPoly: false, selectedUseCase: undefined }),
  })
}

/**
 * Applies a use case preset to the store
 * @param {Object} useCase - The use case to apply, with `id` and `preset`
 */
export const applyUseCasePreset = (id, preset) => {
  const {
    polyCount,
    polygonType,
    material,
    isSmartLowPoly,
    provider,
    targetPolycount: presetTargetPolycount,
  } = preset

  const generationAction = threeDStudioStore.getState().schema.generationAction
  const modelSettings = threeDStudioStore.getState().modelSettings
  const modelSettingsPatch = {
    ...modelSettings,
    ...(polyCount !== undefined && { polyCount }),
    ...(polygonType !== undefined && { polygonType }),
    ...(material !== undefined && { material }),
    ...(presetTargetPolycount !== undefined && { targetPolycount: presetTargetPolycount }),
    isSmartLowPoly,
    // The patch starts from the current settings, so a leftover extraQuality would
    // survive into a LowPoly preset and keep narrowing the engines to Meshy.
    ...(isSmartLowPoly && { extraQuality: false }),
    selectedUseCase: id,
  }

  if (provider) {
    setGenerationProvider(provider)
  }

  const generationModelSchema = getNextGenerationSchema(generationAction, modelSettingsPatch)

  const range = resolveActivePolycountRange(generationModelSchema, {
    polygonType: modelSettingsPatch.polygonType,
    isSmartLowPoly: modelSettingsPatch.isSmartLowPoly,
  })

  // A preset that names no polycount leaves polyCount/targetPolycount alone; the
  // Polycount control re-snaps them when the range changes under it.
  if (presetTargetPolycount === undefined && polyCount !== undefined && range) {
    const { minimum, maximum, default: defaultValue } = range
    modelSettingsPatch.targetPolycount = {
      [POLY_COUNT.LOW]: minimum,
      [POLY_COUNT.MEDIUM]: defaultValue,
      [POLY_COUNT.HIGH]: maximum,
    }[polyCount]
  }

  threeDStudioStore.setState((state) => ({
    schema: {
      ...state.schema,
      ...(generationModelSchema && { generationModelSchema }),
    },
    modelSettings: { ...state.modelSettings, ...modelSettingsPatch },
  }))
}
